// ============================================
// OrderHistory.js — Historial de pedidos
// CORREGIDO: usa CustomerContext, endpoint correcto Medusa v2
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomer } from 'context/CustomerContext';
import './OrderHistory.css';

const OrderHistory = () => {
  const { customer, isAuthenticated, authHeaders } = useCustomer();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrders = useCallback(async () => {
    if (!isAuthenticated) {
      setError('No autenticado');
      setLoading(false);
      return;
    }

    // Si no tenemos customer.id (datos de fallback), no podemos consultar pedidos
    if (!customer?.id) {
      setOrders([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Medusa v2: /store/orders se filtra automáticamente por el customer autenticado.
      // NO enviar customer_id — lo rechaza como "Unrecognized fields".
      const base = process.env.REACT_APP_MEDUSA_BACKEND_URL || 'http://localhost:9000';
      const response = await fetch(`${base}/store/orders`, {
        method: 'GET',
        credentials: 'include',
        headers: authHeaders(),
      });

      if (!response.ok) {
        // NO borrar el token aquí — el error puede ser del endpoint, no de la sesión
        const msg = response.status === 401
          ? 'Tu sesión expiró. Por favor inicia sesión de nuevo.'
          : 'No se pudieron cargar los pedidos';
        throw new Error(msg);
      }

      const data = await response.json();
      setOrders(data.orders || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, customer?.id, authHeaders]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // ─── Formatters ──────────────────────────────────────────────────────────────
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('es-MX', options);
  };

  const formatPrice = (amount, currencyCode = 'MXN') => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount || 0);
  };

  const getFulfillmentStatus = (status) => {
    const labels = {
      not_fulfilled: 'Sin enviar',
      partially_fulfilled: 'Parcialmente enviado',
      fulfilled: 'Enviado',
      partially_shipped: 'Parcialmente enviado',
      shipped: 'En camino',
      partially_returned: 'Parcialmente devuelto',
      returned: 'Devuelto',
      canceled: 'Cancelado',
    };
    return labels[status] || status || 'Pendiente';
  };

  const getPaymentStatus = (status) => {
    const labels = {
      not_paid: 'No pagado',
      awaiting: 'En espera',
      captured: 'Pagado',
      partially_refunded: 'Parcialmente reembolsado',
      refunded: 'Reembolsado',
      canceled: 'Cancelado',
      requires_action: 'Requiere acción',
    };
    return labels[status] || status || 'Pendiente';
  };

  // ─── Estados de UI ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="order-history">
        <div className="order-history__loading">
          <p>Cargando pedidos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-history">
        <div className="order-history__error">
          <p className="order-history__error-text">{error}</p>
          <button className="order-history__btn" onClick={fetchOrders}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="order-history">
        <div className="order-history__empty">
          <p className="order-history__empty-text">
            Actualmente no tienes pedidos.
          </p>
          <a href="/" className="order-history__btn">
            Volver al inicio
          </a>
        </div>
      </div>
    );
  }

  // ─── Lista de pedidos ─────────────────────────────────────────────────────────
  return (
    <div className="order-history">
      <div className="order-history__list">
        {orders.map((order) => (
          <div key={order.id} className="order-history__item">
            <div className="order-history__item-header">
              <div className="order-history__item-info">
                <span className="order-history__order-id">
                  Pedido #{order.display_id || order.id.slice(-8)}
                </span>
                <span className="order-history__date">
                  {formatDate(order.created_at)}
                </span>
              </div>
              <div className="order-history__statuses">
                <span className={`order-history__status order-history__status--${order.fulfillment_status || 'pending'}`}>
                  {getFulfillmentStatus(order.fulfillment_status)}
                </span>
              </div>
            </div>

            <div className="order-history__item-body">
              <div className="order-history__products">
                {order.items?.slice(0, 3).map((item) => (
                  <div key={item.id} className="order-history__product">
                    {item.thumbnail && (
                      <img
                        src={item.thumbnail}
                        alt={item.title}
                        className="order-history__product-image"
                      />
                    )}
                    <div className="order-history__product-info">
                      <span className="order-history__product-name">
                        {item.title || item.product_title}
                      </span>
                      {item.variant?.title && item.variant.title !== 'Default' && (
                        <span className="order-history__product-variant">
                          {item.variant.title}
                        </span>
                      )}
                      <span className="order-history__product-qty">
                        Cantidad: {item.quantity}
                      </span>
                    </div>
                    <span className="order-history__product-price">
                      {formatPrice(item.unit_price * item.quantity, order.currency_code)}
                    </span>
                  </div>
                ))}
                {order.items?.length > 3 && (
                  <p className="order-history__more-items">
                    + {order.items.length - 3} productos más
                  </p>
                )}
              </div>

              <div className="order-history__total">
                <div className="order-history__total-row">
                  <span>Subtotal:</span>
                  <span>{formatPrice(order.subtotal || order.item_total, order.currency_code)}</span>
                </div>
                {order.shipping_total > 0 && (
                  <div className="order-history__total-row">
                    <span>Envío:</span>
                    <span>{formatPrice(order.shipping_total, order.currency_code)}</span>
                  </div>
                )}
                {order.discount_total > 0 && (
                  <div className="order-history__total-row order-history__total-row--discount">
                    <span>Descuento:</span>
                    <span>-{formatPrice(order.discount_total, order.currency_code)}</span>
                  </div>
                )}
                <div className="order-history__total-row order-history__total-row--final">
                  <span>Total:</span>
                  <span className="order-history__total-amount">
                    {formatPrice(order.total, order.currency_code)}
                  </span>
                </div>
              </div>
            </div>

            <div className="order-history__item-footer">
              <div className="order-history__payment-status">
                <span className="order-history__payment-label">Pago:</span>
                <span className={`order-history__payment order-history__payment--${order.payment_status || 'pending'}`}>
                  {getPaymentStatus(order.payment_status)}
                </span>
              </div>
              <button
                className="order-history__view-btn"
                onClick={() => navigate(`/account/orders/${order.id}`)}
              >
                Ver detalles
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrderHistory;
